import { getPricingCopy } from './pricingCopy.js';

// Published price lists are independent currencies, not exchange-rate conversions.
export const PRICING_RATES = {
  tr: {
    currency: 'TRY', plans: { live: 1500, starter: 4500, standard: 8000, pro: 18500, elite: 50000 },
    seat: 1500, channel: 1000, automation: 2000, whatsapp: 4000, sends: { marketing: 40, utility: 6 },
    messages: [300, 200, 100], support: [0, 5000, 12500, 50000], setup: 15000, setupOriginal: 50000, hourly: 3500,
  },
  en: {
    currency: 'USD', plans: { live: 30, starter: 89, standard: 159, pro: 369, elite: 1000 },
    seat: 30, channel: 20, automation: 40, whatsapp: 79, sends: { marketing: 0.7, utility: 0.12 },
    messages: [6, 4, 2], support: [0, 99, 249, 1000], setup: 300, setupOriginal: 1000, hourly: 70,
  },
};

export const PLAN_LIMITS = [
  { id: 'live', messages: 0, seats: 1, channels: 1, support: 'essential', features: 3 },
  { id: 'starter', messages: 500, seats: 1, channels: 1, support: 'essential', features: 2 },
  { id: 'standard', messages: 1000, seats: 2, channels: 2, automations: 1, support: 'essential', features: 2 },
  { id: 'pro', messages: 3000, seats: 5, channels: 4, automations: 4, support: 'priority', features: 4 },
  { id: 'elite', messages: 10000, seats: 10, channels: 10, automations: 10, support: 'premium', features: 4 },
];

export const SUPPORT_LIMITS = [
  { id: 'essential', response: 48, hours: '5×8', work: 0, features: 2 },
  { id: 'priority', response: 8, hours: '5×8', work: 3, features: 3 },
  { id: 'premium', response: 4, hours: '7×8', work: 6, features: 3 },
  { id: 'critical', response: 1, hours: '7×24', work: 16, features: 2 },
];

export function fillPricingLabel(label, values) {
  return label.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
}

export function formatPricingMoney(amount, locale, minimumFractionDigits = 0) {
  const language = locale === 'tr' ? 'tr' : 'en';
  const number = new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits, maximumFractionDigits: 2 });
  return `${language === 'tr' ? '₺' : '$'}${number.format(amount)}`;
}

export function getPricingData(t, locale) {
  const language = locale === 'tr' ? 'tr' : 'en';
  const rates = PRICING_RATES[language];
  const number = new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-US', { maximumFractionDigits: 2 });
  const money = amount => formatPricingMoney(amount, language);
  const sendPrices = {
    marketing: formatPricingMoney(rates.sends.marketing, language, language === 'tr' ? 0 : 2),
    utility: formatPricingMoney(rates.sends.utility, language, language === 'tr' ? 0 : 2),
  };
  const copy = getPricingCopy(t, { setup: money(rates.setup), automation: money(rates.automation), sends: sendPrices }, language);
  const features = (prefix, count) => Array.from({ length: count }, (_, i) => copy[`${prefix}.feature${i + 1}`]);
  const plans = PLAN_LIMITS.map(plan => ({
    ...plan,
    name: copy[`plans.${plan.id}.name`],
    description: copy[`plans.${plan.id}.description`],
    price: money(rates.plans[plan.id]),
    messages: plan.messages === null ? copy.customQuota : plan.messages === 0 ? copy.noAi : number.format(plan.messages),
    seats: plan.seats === null ? copy.upToUnlimited : number.format(plan.seats),
    channels: plan.channels === null ? copy.upToUnlimited : `${plan.channels}${plan.id === 'live' || plan.id === 'starter' ? ' · Webchat' : ''}`,
    support: plan.support ? fillPricingLabel(copy.includedSupport, { support: copy[`support.${plan.support}.name`] }) : null,
    features: features(`plans.${plan.id}`, plan.features),
  }));
  const support = SUPPORT_LIMITS.map((plan, index) => ({
    ...plan,
    name: copy[`support.${plan.id}.name`],
    price: rates.support[index] === 0 ? copy['support.free'] : money(rates.support[index]),
    response: fillPricingLabel(copy['support.hourCount'], { hours: plan.response }),
    value: plan.work ? fillPricingLabel(copy['support.value'], { price: money(plan.work * rates.hourly) }) : null,
    work: plan.work ? fillPricingLabel(copy['support.hourCount'], { hours: plan.work }) : copy['support.none'],
    included: copy[`support.${plan.id}Included`],
    features: features(`support.${plan.id}`, plan.features),
  }));
  const faq = ['vat', 'setup', 'automation', 'whatsapp', 'support'].map(id => ({
    question: copy[`faq.${id}.question`], answer: copy[`faq.${id}.answer`],
  }));
  return { copy, plans, support, faq, rates, money, sendPrices };
}
